/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1705622400000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Fri Jan 19 2024 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "azure-blob-destination",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Azure Blob Storage",
          "depth": 0,
          "id": "connect-to-azure-blob-storage"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Select file format",
          "depth": 1,
          "id": "select-file-format"
        }, {
          "value": "Enter filename",
          "depth": 1,
          "id": "enter-filename"
        }, {
          "value": "Set filename offset",
          "depth": 1,
          "id": "set-filename-offset"
        }, {
          "value": "Columns to sync",
          "depth": 1,
          "id": "columns-to-sync"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      ul: "ul",
      li: "li",
      strong: "strong",
      a: "a",
      img: "img",
      h3: "h3",
      code: "code",
      em: "em"
    }, _provideComponents(), props.components), {RowAndColumnOrderingInfo, NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!RowAndColumnOrderingInfo) _missingMdxReference("RowAndColumnOrderingInfo", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "With Hightouch, you can export all the data in your warehouse to JSON, CSV, or Parquet files in any of your Azure Blob Storage containers. Whether it's for internal processes, analytics, or product data, our Azure Blob Storage integration allows you to securely and easily transfer data at web-scale."
      }), "\n", _jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Any data set"
            }), _jsx(_components.td, {
              children: "Sync data from a source to Azure Blob Storage as JSON, CSV, or Parquet files"
            }), _jsx(_components.td, {
              children: "Insert, All, Diff"
            })]
          })
        })]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "All"
          }), ": All mode creates one file with all the rows in the query results, every time the sync runs."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Insert"
          }), ": Insert mode creates one file with the rows that were added since the last sync."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Diff"
          }), ": Diff mode creates three files, one for rows added, one for rows changed, and another for rows removed since the last sync."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(RowAndColumnOrderingInfo, {
        variant: "file"
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-azure-blob-storage",
        children: "Connect to Azure Blob Storage"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations?from=docs",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Azure Blob Storage"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), ". You can then authenticate Hightouch to ", _jsx(_components.strong, {
          children: "Azure Blob Storage"
        }), " using Azure credentials. You can select credentials you previously created or click ", _jsx(_components.strong, {
          children: "New credentials"
        }), " to set them up."]
      }), "\n", _jsxs(_components.p, {
        children: ["See the guide for ", _jsx(_components.a, {
          href: "/security/azure",
          children: "configuring Azure credentials"
        }), " to learn how you can set up a service account and use its credentials. The IAM user whose credentials you use must have programmatic access enabled and permission to write to the Azure Blob Storage container you want to use."]
      }), "\n", _jsxs(_components.p, {
        children: ["Next, enter your Blob Storage ", _jsx(_components.strong, {
          children: "Account name"
        }), " and ", _jsx(_components.strong, {
          children: "Container name"
        }), " to complete setup.\nYou can find these in the ", _jsx(_components.a, {
          href: "https://portal.azure.com/",
          children: "Azure Portal"
        }), ".\nGo to ", _jsx(_components.strong, {
          children: "Storage accounts"
        }), " and open the storage account you want to use.\nThe account name is in the top left above ", _jsx(_components.strong, {
          children: "Storage account"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-azure-blob-storage-account-container-name.png",
          alt: "Azure storage containers UI"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["You can see your container names once you open ", _jsx(_components.strong, {
          children: "Containers"
        }), " in the left side menu.\nIf you want to create a new container for Hightouch to sync to, you need ", _jsx(_components.a, {
          href: "https://learn.microsoft.com/en-us/azure/storage/blobs/storage-quickstart-blobs-portal#create-a-container",
          children: "create it"
        }), " before you can proceed."]
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your Azure Blob Storage destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs?from=docs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the Azure Blob Storage destination you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "select-file-format",
        children: "Select file format"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports syncing JSON, CSV, and Parquet files to Azure Blob Storage."
      }), "\n", _jsx(_components.h3, {
        id: "enter-filename",
        children: "Enter filename"
      }), "\n", _jsxs(_components.p, {
        children: ["The filename field lets you specify the parent directory and the name of the file you want to use for your results.\nYou can include timestamp variables in the filename, surrounding each with ", _jsx(_components.code, {
          children: "{}"
        }), ".\nHightouch supports these timestamp variables:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "YYYY"
          }), ": Represents the full year in four digits."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "YY"
          }), ": The last two digits of the year."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "MM"
          }), ": Two-digit month format (01-12)."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "DD"
          }), ": Two-digit day format (01-31)."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "HH"
          }), ": Two-digit hour format in 24-hour clock (00-23)."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "mm"
          }), ": Two-digit minute format (00-59)."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "ss"
          }), ": Two-digit second format (00-59)."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "ms"
          }), ": Three-digit millisecond format."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "X"
          }), ": Unix timestamp in seconds."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "x"
          }), ": Unix timestamp in milliseconds."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "All dates and times are UTC."
      }), "\n", _jsxs(_components.p, {
        children: ["For example, you could enter ", _jsx(_components.code, {
          children: "upload/{YYYY}-{MM}-{DD}-{HH}-{mm}-result.json"
        }), " to dynamically include the year, month, date, hour, and minute in each uploaded file. Hightouch would insert each file in the ", _jsx(_components.code, {
          children: "upload"
        }), " directory, which would need to already exist in your bucket."]
      }), "\n", _jsxs(_components.p, {
        children: ["You can also use other ", _jsx(_components.a, {
          href: "/syncs/mapping-data#variable-values",
          children: "variable values"
        }), " to include sync metadata in the filename:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "{model.id}"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "{model.name}"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "{sync.id}"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "{sync.run.id}"
          })
        }), "\n"]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["If a file already exists at the path you entered at the time of a sync, Hightouch overwrites it. To view different versions of the same file in Azure Blob Storage, you can right-click the file and select ", _jsx(_components.strong, {
            children: "View versions"
          }), "."]
        })
      }), "\n", _jsxs(Tip, {
        children: ["If you are using an audience and would like to include the audience name, you will still use ", _jsx(_components.code, {
          children: "{model.name}"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "set-filename-offset",
        children: "Set filename offset"
      }), "\n", _jsxs(_components.p, {
        children: ["By default, Hightouch uses the timestamp of the sync run to fill in timestamp variables. You can optionally include an offset in seconds. For example, if you want the filename's date to be 24 hours ", _jsx(_components.em, {
          children: "before"
        }), " the sync takes place, enter ", _jsx(_components.code, {
          children: "-86400"
        }), " (", _jsx(_components.code, {
          children: "24 hours * 60 minutes * 60 seconds"
        }), "). If you want the filename's data to be one hour after the sync takes place, you would enter ", _jsx(_components.code, {
          children: "3600"
        }), " (", _jsx(_components.code, {
          children: "60 minutes * 60 seconds"
        }), ")."]
      }), "\n", _jsx(_components.h3, {
        id: "columns-to-sync",
        children: "Columns to sync"
      }), "\n", _jsx(_components.p, {
        children: "You can export all columns exactly as your model returns them or choose to export specific ones."
      }), "\n", _jsxs(_components.p, {
        children: ["If you need to rename or transform any column values you're syncing, you can use the ", _jsx(_components.a, {
          href: "/syncs/mapping-data#advanced-mapper",
          children: "advanced mapper"
        }), " to do so. If you choose this option, Hightouch only syncs the fields you explicitly map."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-azure-blob-storage-add-mappings.png",
          alt: "Exporting and remapping select columns in your model"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["The preceding example shows how to selectively export the ", _jsx(_components.code, {
          children: "first_name"
        }), ", ", _jsx(_components.code, {
          children: "email"
        }), ", and ", _jsx(_components.code, {
          children: "last_name"
        }), " fields. Hightouch exports these fields to new fields in the file and ignores all other columns from your results."]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
